import { create } from 'zustand';
import { User } from '../types';
import { authApi } from '../api/auth';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean; // true after initial user fetch attempt
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, correspondenceLanguage?: string, referralCode?: string, visitorId?: string) => Promise<void>;
  logout: () => void;
  fetchCurrentUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('authToken'),
  isAuthenticated: !!localStorage.getItem('authToken'),
  isInitialized: !localStorage.getItem('authToken'), // if no token, already initialized

  login: async (email, password) => {
    try {
      const data = await authApi.login({ email, password });
      localStorage.setItem('authToken', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      set({ user: data.user, token: data.access_token, isAuthenticated: true });
    } catch (error) {
      // Ensure we don't set any state on error
      console.error('AuthStore login error:', error);
      throw error; // Re-throw to let the component handle it
    }
  },

  register: async (email, password, correspondenceLanguage, referralCode, visitorId) => {
    try {
      const data = await authApi.register({ email, password, correspondenceLanguage, referralCode, visitorId });
      localStorage.setItem('authToken', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      set({ user: data.user, token: data.access_token, isAuthenticated: true });
    } catch (error) {
      // Ensure we don't set any state on error
      console.error('AuthStore register error:', error);
      throw error; // Re-throw to let the component handle it
    }
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    set({ user: null, token: null, isAuthenticated: false });
  },

  fetchCurrentUser: async () => {
    try {
      const user = await authApi.getCurrentUser();
      set({ user, isAuthenticated: true, isInitialized: true });
    } catch (error) {
      localStorage.removeItem('authToken');
      set({ user: null, token: null, isAuthenticated: false, isInitialized: true });
    }
  },
}));
